import { bootstrapApplication } from '@angular/platform-browser';
import { registerLicense } from '@syncfusion/ej2-base';
import { AppComponent } from 'app/app.component';
import { appConfig } from 'app/app.config';

// Register the Syncfusion license once, globally, before anything renders.
// Previously this was registered ad-hoc inside individual components, so any
// Syncfusion widget that rendered before one of those components evaluated
// (e.g. reply-editor, email-thread-details, chat-drawer, sidebar) would show
// the "trial version" banner. Registering at bootstrap guarantees it is always
// set first. registerLicense() only pulls in @syncfusion/ej2-base (the small
// core), not the heavy editor runtime, so deferred loading is unaffected.
registerLicense(
    'Ngo9BigBOggjHTQxAR8/V1NNaF5cXmBCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdlWXleeHVcRWddVE13X0dWYUA=',
);

// Clean up corrupted localStorage data before app initialization
function cleanupLocalStorage(): void {
    try {
        // Version-based cache clearing
        const CURRENT_VERSION = '1.0.1'; // Increment this to force clear old localStorage
        const storedVersion = localStorage.getItem('app_version');
        
        if (storedVersion !== CURRENT_VERSION) {
            console.warn(`App version changed from ${storedVersion} to ${CURRENT_VERSION}, clearing cache`);
            // Keep accessToken and userData, but clear everything else
            const accessToken = localStorage.getItem('accessToken');
            const userData = localStorage.getItem('userData');
            const userData_tradelist = localStorage.getItem('userData_tradelist');
            
            // Clear all localStorage
            localStorage.clear();
            
            // Restore critical auth data if it's valid
            if (accessToken && accessToken !== 'undefined' && accessToken !== 'null') {
                localStorage.setItem('accessToken', accessToken);
            }
            if (userData && userData !== 'undefined' && userData !== 'null') {
                try {
                    JSON.parse(userData); // Validate it's valid JSON
                    localStorage.setItem('userData', userData);
                } catch (e) {
                    console.warn('userData was invalid, not restoring');
                }
            }
            if (userData_tradelist && userData_tradelist !== 'undefined' && userData_tradelist !== 'null') {
                try {
                    JSON.parse(userData_tradelist);
                    localStorage.setItem('userData_tradelist', userData_tradelist);
                } catch (e) {
                    console.warn('userData_tradelist was invalid, not restoring');
                }
            }
            
            // Set new version
            localStorage.setItem('app_version', CURRENT_VERSION);
        }
        
        // Check and clean userData
        const userData = localStorage.getItem('userData');
        if (userData && (userData === 'undefined' || userData === 'null')) {
            console.warn('Removing corrupted userData from localStorage');
            localStorage.removeItem('userData');
            localStorage.removeItem('accessToken');
        } else if (userData) {
            // Try to parse it to ensure it's valid JSON
            try {
                JSON.parse(userData);
            } catch (e) {
                console.warn('Invalid userData JSON, clearing localStorage');
                localStorage.removeItem('userData');
                localStorage.removeItem('accessToken');
            }
        }

        // Check and clean SMS conversation cache
        const smsConversations = localStorage.getItem('sms_conversations');
        if (smsConversations && (smsConversations === 'undefined' || smsConversations === 'null')) {
            console.warn('Removing corrupted sms_conversations from localStorage');
            localStorage.removeItem('sms_conversations');
        } else if (smsConversations) {
            try {
                JSON.parse(smsConversations);
            } catch (e) {
                console.warn('Invalid sms_conversations JSON, clearing');
                localStorage.removeItem('sms_conversations');
            }
        }

        // Check and clean deleted SMS chats
        const deletedSmsChats = localStorage.getItem('deleted_sms_chats');
        if (deletedSmsChats && (deletedSmsChats === 'undefined' || deletedSmsChats === 'null')) {
            console.warn('Removing corrupted deleted_sms_chats from localStorage');
            localStorage.removeItem('deleted_sms_chats');
        } else if (deletedSmsChats) {
            try {
                JSON.parse(deletedSmsChats);
            } catch (e) {
                console.warn('Invalid deleted_sms_chats JSON, clearing');
                localStorage.removeItem('deleted_sms_chats');
            }
        }
    } catch (error) {
        console.error('Error during localStorage cleanup:', error);
    }
}

// Run cleanup before app initialization
cleanupLocalStorage();

// Fix for non-passive event listener violations
// This makes all touch and wheel events passive by default to improve performance
let passiveSupported = false;
try {
    const options = {
        get passive() {
            passiveSupported = true;
            return false;
        }
    };
    (window as any).addEventListener('test', null, options);
    (window as any).removeEventListener('test', null, options);
} catch (err) {
    passiveSupported = false;
}

// Override addEventListener to make touch and wheel events passive by default
const originalAddEventListener = EventTarget.prototype.addEventListener;
EventTarget.prototype.addEventListener = function(type, listener, options) {
    if (passiveSupported && (type === 'touchstart' || type === 'touchmove' || type === 'wheel' || type === 'mousewheel')) {
        if (typeof options === 'boolean') {
            options = { capture: options, passive: true };
        } else if (typeof options === 'object' && options !== null) {
            options = { ...options, passive: true };
        } else {
            options = { passive: true };
        }
    }
    return originalAddEventListener.call(this, type, listener, options);
};

// Service Worker registration is now handled by `provideServiceWorker()`
// in `app.config.ts` so Angular's `SwUpdate` receives version events.
// `AppUpdateService` (wired in `AppComponent.ngOnInit`) listens for
// `VersionReadyEvent` and prompts the user with a "Refresh now"
// snackbar. The old manual `navigator.serviceWorker.register` + raw
// `setInterval` pattern lived here; it registered the SW outside
// Angular's DI graph so `SwUpdate` was never wired up, leaving users
// indefinitely stuck on cached bundles after a deploy.
bootstrapApplication(AppComponent, appConfig)
    .catch((err) => console.error(err));
